import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
const toNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

/**
 * @typedef {Object} VariantPayload
 * @property {string} id
 * @property {boolean} available
 * @property {boolean | string | null} inventory_management
 * @property {Array<string>} options
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {string} [restock_date]
 * @property {number} [inventory_quantity]
 * @property {string} [inventory_policy]
 * @property {{ id?: string, preview_image?: { src?: string } }} [featured_media]
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Auto-select variant if there are hidden single options
    // This ensures variants are selected even when options are hidden via hide_single_option
    requestAnimationFrame(() => {
      this.autoSelectHiddenOptions();
    });
  }
  
  /**
   * Auto-selects variants when single options are hidden.
   * This ensures the variant is selected even when options are not rendered.
   */
  autoSelectHiddenOptions() {
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (!scriptTag || !scriptTag.textContent) return;
    
    try {
      const variant = JSON.parse(scriptTag.textContent);
      if (!variant || !variant.id || !variant.options) return;
      
      // Check if we have any selected options
      const selectedOptions = Array.from(this.querySelectorAll('fieldset input:checked, select option[selected]'));
      
      // If no options are selected but we have a variant with options, auto-select them
      if (selectedOptions.length === 0 && variant.options.length > 0) {
        // Find and select the matching option for each variant option value
        variant.options.forEach((optionValue) => {
          // Try to find radio input first
          const radioInput = this.querySelector(`input[type="radio"][value="${CSS.escape(optionValue)}"]`);
          if (radioInput instanceof HTMLInputElement && !radioInput.checked) {
            radioInput.checked = true;
            // Trigger change event to update variant state
            this.variantChanged(new Event('change', { bubbles: true, target: radioInput }));
            return;
          }
          
          // Try to find select option
          const selectOption = Array.from(this.querySelectorAll('select option')).find(
            (opt) => opt instanceof HTMLOptionElement && opt.value === optionValue
          );
          if (selectOption instanceof HTMLOptionElement) {
            const select = selectOption.closest('select');
            if (select instanceof HTMLSelectElement && select.value !== optionValue) {
              select.value = optionValue;
              // Trigger change event to update variant state
              this.variantChanged(new Event('change', { bubbles: true, target: select }));
            }
          }
        });
      }
    } catch (error) {
      // Silently fail if JSON parsing fails
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const optionGroupCount = this.querySelectorAll('fieldset, .variant-option--dropdowns').length;
    const hasMultipleOptionGroups = optionGroupCount > 1;
    const needsFetchForMultiOption =
      isOnProductPage &&
      hasMultipleOptionGroups &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    const needsFetch =
      loadsNewProduct ||
      needsFetchForMultiOption ||
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component');

    if (needsFetch) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, selectedOption);
    } else {
      this.handleLocalVariantChange(selectedOption);
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Handles variant change locally without fetching from server.
   * @param {HTMLElement} selectedOption - The selected option element.
   */
  handleLocalVariantChange(selectedOption) {
    const variantId = selectedOption.dataset.variantId;
    if (!variantId) return;

    const scriptTag = this.querySelector('script[type="application/json"]');
    if (!scriptTag) return;

    let existingVariant = null;

    if (scriptTag.textContent) {
      try {
        const parsedVariant = JSON.parse(scriptTag.textContent);

        if (parsedVariant && typeof parsedVariant === 'object') {
          const parsedOptions = Array.isArray(parsedVariant.options) ? parsedVariant.options : [];
          const price = toNumber(parsedVariant.price);
          const compareAtPrice = toNumber(parsedVariant.compare_at_price);
          const unitPrice = toNumber(parsedVariant.unit_price);

          // CRITICAL: Ensure available is properly converted to boolean
          // It might come as string "true"/"false" or boolean true/false from JSON
          let availableValue = parsedVariant.available;
          if (typeof availableValue === 'string') {
            availableValue = availableValue === 'true';
          } else {
            availableValue = Boolean(availableValue);
          }
          
          existingVariant = {
            ...parsedVariant,
            id: typeof parsedVariant.id === 'number' ? String(parsedVariant.id) : String(parsedVariant.id || ''),
            available: availableValue,
            inventory_management: Boolean(parsedVariant.inventory_management),
            options: parsedOptions,
          };

          if (price !== undefined) existingVariant.price = price;
          if (compareAtPrice !== undefined) existingVariant.compare_at_price = compareAtPrice;
          if (unitPrice !== undefined) existingVariant.unit_price = unitPrice;
        }
      } catch (error) {
        existingVariant = null;
      }
    }

    const variant = this.buildVariantFromOptions(existingVariant);
    if (!variant) return;

    scriptTag.textContent = JSON.stringify(variant);
    this.updateSizeLegend(selectedOption);

    const eventPayload = {
      html: document,
      productId: this.dataset.productId ?? '',
      newProduct: undefined,
    };
    const updateEvent = new VariantUpdateEvent(variant, this.selectedOptionId ?? '', eventPayload);

    this.dispatchEvent(updateEvent);
  }

  /**
   * Builds variant object from currently selected options.
   * @param {VariantPayload | null} existingVariant - The previous variant payload, if available.
   * @returns {VariantPayload | null} The variant object or null if not found.
   */
  buildVariantFromOptions(existingVariant = null) {
    const selectedOptions = Array.from(this.querySelectorAll('fieldset input:checked, select option[selected]'));

    if (selectedOptions.length === 0) {
      return existingVariant;
    }

    const selectedValues = selectedOptions.map((opt) => {
      if (opt instanceof HTMLInputElement || opt instanceof HTMLOptionElement) {
        return opt.value;
      }
      return opt.textContent?.trim() || '';
    });

    // For multi-option products (e.g. bundles), find the variant that matches the full combination
    // of selected options. Each input's data-variant-id may reference a different combination.
    // Single-option products: also resolve from data-variants by variant id for accurate compare_at.
    const variantsData = this.dataset.variants;
    if (variantsData && selectedOptions.length >= 1) {
      try {
        const variants = JSON.parse(variantsData);
        const matchingVariant =
          selectedOptions.length === 1 && selectedOptions[0] instanceof HTMLElement
            ? variants.find((v) => String(v.id) === String(selectedOptions[0].dataset.variantId))
            : variants.find(
                (v) =>
                  Array.isArray(v.options) &&
                  v.options.length === selectedValues.length &&
                  v.options.every((opt, i) => opt === selectedValues[i])
              );

        if (matchingVariant && matchingVariant.id) {
          const price = toNumber(matchingVariant.price);
          const compareAtPrice = toNumber(matchingVariant.compare_at_price);
          const inventoryQuantity = matchingVariant.inventory_quantity;
          const inventoryPolicy = matchingVariant.inventory_policy;
          let inventoryManagement = matchingVariant.inventory_management;
          if (inventoryManagement === true) inventoryManagement = 'shopify';
          else if (!inventoryManagement) inventoryManagement = null;

          return {
            ...(existingVariant ?? {}),
            id: String(matchingVariant.id),
            available: Boolean(matchingVariant.available),
            inventory_management: inventoryManagement,
            options: selectedValues,
            price: price ?? toNumber(existingVariant?.price),
            compare_at_price: compareAtPrice !== undefined ? compareAtPrice : null,
            inventory_quantity: inventoryQuantity ?? existingVariant?.inventory_quantity,
            inventory_policy: inventoryPolicy ?? existingVariant?.inventory_policy,
            restock_date: matchingVariant.restock_date ?? existingVariant?.restock_date,
            featured_media: matchingVariant.featured_media ?? existingVariant?.featured_media,
          };
        }
      } catch (e) {
        // Fall through to default logic
      }
    }

    const variantOption = selectedOptions.find((opt) => {
      if (opt instanceof HTMLElement) {
        return opt.dataset.variantId;
      }
      return false;
    });

    if (!variantOption || !(variantOption instanceof HTMLElement) || !variantOption.dataset.variantId) return null;

    const parsedVariantId = Number.parseInt(variantOption.dataset.variantId, 10);
    if (Number.isNaN(parsedVariantId)) return null;

    // CRITICAL: Check availability from option data attribute
    // data-option-available outputs "true" or "false" as strings from Liquid
    const optionAvailable = variantOption.dataset.optionAvailable;
    // Convert string "true"/"false" to boolean, or use existing boolean
    const available = optionAvailable === 'true' || optionAvailable === true || optionAvailable === 'True';
    const baseVariant = existingVariant && typeof existingVariant === 'object' ? existingVariant : null;
    const price = toNumber(variantOption.dataset.variantPrice);
    const compareAtPrice = toNumber(variantOption.dataset.variantComparePrice);
    const restockDate = variantOption.dataset.restockDate;
    const inventoryQuantity = variantOption.dataset.inventoryQuantity
      ? Number.parseInt(variantOption.dataset.inventoryQuantity, 10)
      : undefined;
    const inventoryPolicy = variantOption.dataset.inventoryPolicy;
    const inventoryManagement = variantOption.dataset.inventoryManagement;

    const fallbackInventoryManagement =
      baseVariant && 'inventory_management' in baseVariant
        ? baseVariant.inventory_management
        : null;
    
    // Preserve inventory_management as string ('shopify' or null), not boolean
    // If baseVariant has it as boolean true, convert to 'shopify', otherwise use string value
    let finalInventoryManagement = inventoryManagement || null;
    if (!finalInventoryManagement && fallbackInventoryManagement !== null && fallbackInventoryManagement !== undefined) {
      if (typeof fallbackInventoryManagement === 'string') {
        finalInventoryManagement = fallbackInventoryManagement;
      } else if (fallbackInventoryManagement === true) {
        finalInventoryManagement = 'shopify';
      } else {
        finalInventoryManagement = null;
      }
    }

    // CRITICAL: Ensure available is always a boolean
    // Option data takes precedence (most accurate for currently selected option)
    // Fallback to base variant's available if option data is missing
    let finalAvailable = available; // Already converted to boolean above
    if (typeof finalAvailable === 'undefined' && baseVariant) {
      // Fallback to base variant's available if option data wasn't available
      if (typeof baseVariant.available === 'string') {
        finalAvailable = baseVariant.available === 'true';
      } else {
        finalAvailable = Boolean(baseVariant.available);
      }
    }
    // Ensure it's always a boolean (default to false if somehow still undefined)
    finalAvailable = Boolean(finalAvailable);
    
    const mergedVariant = {
      ...(baseVariant ?? {}),
      id: variantOption.dataset.variantId || '',
      available: finalAvailable,
      inventory_management: finalInventoryManagement,
      options: selectedOptions.map((opt) => {
        if (opt instanceof HTMLInputElement || opt instanceof HTMLOptionElement) {
          return opt.value;
        }

        return opt.textContent?.trim() || '';
      }),
    };

    const fallbackPrice = baseVariant ? toNumber(baseVariant.price) : undefined;
    const fallbackInventoryQuantity =
      baseVariant && 'inventory_quantity' in baseVariant ? baseVariant.inventory_quantity : undefined;
    const fallbackInventoryPolicy =
      baseVariant && 'inventory_policy' in baseVariant ? baseVariant.inventory_policy : undefined;

    if (price !== undefined) {
      mergedVariant.price = price;
    } else if (fallbackPrice !== undefined) {
      mergedVariant.price = fallbackPrice;
    }

    if (compareAtPrice !== undefined) {
      mergedVariant.compare_at_price = compareAtPrice;
    } else {
      mergedVariant.compare_at_price = null;
    }

    if (restockDate) {
      mergedVariant.restock_date = restockDate;
    } else {
      delete mergedVariant.restock_date;
    }

    if (inventoryQuantity !== undefined) {
      mergedVariant.inventory_quantity = inventoryQuantity;
    } else if (fallbackInventoryQuantity !== undefined) {
      mergedVariant.inventory_quantity = fallbackInventoryQuantity;
    }

    if (inventoryPolicy) {
      mergedVariant.inventory_policy = inventoryPolicy;
    } else if (fallbackInventoryPolicy !== undefined) {
      mergedVariant.inventory_policy = fallbackInventoryPolicy;
    }

    return mergedVariant;
  }

  /**
   * Updates the size legend text immediately.
   * @param {HTMLElement} selectedOption - The selected option element.
   */
  updateSizeLegend(selectedOption) {
    const fieldset = selectedOption.closest('fieldset');
    if (!fieldset) return;

    const legendText = fieldset.querySelector('legend .variant-option__button-label__text');
    if (!(legendText instanceof HTMLElement)) return;

    if (!(selectedOption instanceof HTMLInputElement)) return;
    const optionName = selectedOption.name;
    if (!optionName?.toLowerCase().includes('size')) return;

    const humanSize = selectedOption.dataset.humanLabel ?? selectedOption.value;
    const sizePrefix = legendText.dataset.sizeLabelPrefix ?? '';
    legendText.textContent = sizePrefix ? `${sizePrefix}: ${humanSize}` : humanSize;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {HTMLElement} [fallbackSelectedOption] - Fallback to handleLocalVariantChange when fetch fails (e.g. 429).
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, fallbackSelectedOption = null) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          if (fallbackSelectedOption) {
            this.handleLocalVariantChange(fallbackSelectedOption);
          }
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error('Variant picker re-render failed:', error);
          if (fallbackSelectedOption) {
            this.handleLocalVariantChange(fallbackSelectedOption);
          }
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      console.error('[Variant Picker] No option value ID found on selected option');
      // Return empty string instead of throwing to prevent crash
      return '';
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
